import { useCallback, useEffect, useMemo, useState } from "react";

import { Box, Collapse, Tabs } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { LayoutStandard, Loader } from "components";

import { IRootState } from "config/redux";
import { AuthorityConstants, RouteConstants, ServerConstants, ServiceConstants } from "constant";

import { BankCreditService, CreditService, UserService } from "services";
import {
  Bank,
  BankCreditOptionsResponse,
  BankCreditResponse,
  ClientResponse,
  CreditOptionsResponse,
  CreditResponse,
  UserResponse,
} from "types";

import { AppUtils } from "utils";

import { useAutomaticBankCreditNotes } from "./bank-credit/automatic-notes/useAutomaticBankCreditNotes";
import { BankCreditRequestHeader } from "./bank-credit/bank-credit-request-header";
import { BankCreditRequest } from "./bank-credit/request/bank-credit-request";
import { BankSelection } from "./bank-selection";
import { BankSelectionHeader } from "./bank-selection/bank-selection-header";
import { ClientDetails } from "./client-details";
import ClientCredit from "./credit";
import { CreditPreparationHeader } from "./credit/credit-preparation-header";

import { PreviousCreditRequests } from "./previous-credits/previous-credit-requests";

import classes from "./previous-credits.module.css";

const { ROUTE_ERROR_406, ROUTE_MY_CLIENTS } = RouteConstants;
const {
  REQUEST_USERS_CLIENTS_VIEW_ID,
  REQUEST_CREDIT_VIEW_ID,
  REQUEST_CREDIT_NEW_ID,
  REQUEST_CREDIT_OPTIONS_ID,
  REQUEST_CREDIT_STATUS_CHANGE_ID,
  REQUEST_CREDIT_REQUEST_ID,
  REQUEST_CREDIT_UPDATE_MULTIPLE_PROPERTIES_ID,
  REQUEST_PENDING,
  REQUEST_CREDIT_POSTPONE_INSTALLMENT_ID,
  REQUEST_BANK_CREDIT_VIEW_ID,
  REQUEST_BANK_CREDIT_NEW_ID,
  REQUEST_BANK_CREDIT_OPTIONS_ID,
  REQUEST_BANK_CREDIT_UPDATE_MULTIPLE_PROPERTIES_ID,
} = ServiceConstants;
const { EXCEPTION_IDENTITY_NOT_FOUND } = ServerConstants;

enum TABS {
  CURRENT_REQUEST = "current-request",
  PREVIOUS_REQUESTS = "previous-requests",
}

const ClientViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const agent = window.location.pathname.startsWith(ROUTE_MY_CLIENTS);
  const clientServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_USERS_CLIENTS_VIEW_ID],
  );
  const creditServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_VIEW_ID],
  );
  const creditNewServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_NEW_ID],
  );
  const creditOptionsServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_OPTIONS_ID],
  );
  const creditStatusServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_STATUS_CHANGE_ID],
  );
  const creditRequestServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_REQUEST_ID],
  );
  const creditUpdateMultipleServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_UPDATE_MULTIPLE_PROPERTIES_ID],
  );
  const postponeInstallmentServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_POSTPONE_INSTALLMENT_ID],
  );
  const bankCreditServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_BANK_CREDIT_VIEW_ID],
  );
  const bankCreditNewServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_BANK_CREDIT_NEW_ID],
  );
  const bankCreditOptionsServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_BANK_CREDIT_OPTIONS_ID],
  );
  const bankCreditUpdateMultipleServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_BANK_CREDIT_UPDATE_MULTIPLE_PROPERTIES_ID],
  );
  const [client, setClient] = useState<UserResponse | undefined>(undefined);
  const [credit, setCredit] = useState<CreditResponse | undefined>(undefined);
  const [bankCredit, setBankCredit] = useState<BankCreditResponse | undefined>(undefined);
  const [options, setOptions] = useState<CreditOptionsResponse | undefined>(undefined);
  const [bankCreditOptions, setBankCreditOptions] = useState<BankCreditOptionsResponse | undefined>(
    undefined,
  );
  const [unreadMessages, setUnreadMessages] = useState<number | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<string | null>(TABS.CURRENT_REQUEST);
  const [previousRequestSelectedId, setPreviousRequestSelectedId] = useState<number | null>(null);
  const [isCreditPreparationExpanded, setIsCreditPreparationExpanded] = useState(true);
  const [isBankCreditExpanded, setIsBankCreditExpanded] = useState(false);
  const [isBankSelectionExpanded, setIsBankSelectionExpanded] = useState(false);

  useAutomaticBankCreditNotes(bankCredit);

  useEffect(() => {
    UserService.userClientsOne(id!);
    CreditService.creditOptions();
    BankCreditService.bankCreditOptions();
  }, [id]);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      clientServiceObject,
      REQUEST_USERS_CLIENTS_VIEW_ID,
      (val: ClientResponse) => {
        setClient(val.user);
        setCredit(val.credit);
        setUnreadMessages(val.unreadMessages);
      },
      (err) => {
        if (err?.data?.code === EXCEPTION_IDENTITY_NOT_FOUND) {
          navigate(ROUTE_ERROR_406);
        }
      },
    );
    AppUtils.resolveServiceObject(creditStatusServiceObject, REQUEST_CREDIT_STATUS_CHANGE_ID, () => {
      if (id) {
        UserService.userClientsOne(id);
      }
    });
    AppUtils.resolveServiceObject(creditRequestServiceObject, REQUEST_CREDIT_REQUEST_ID, () => {
      if (id) {
        UserService.userClientsOne(id);
      }
    });
    AppUtils.resolveServiceObject(creditServiceObject, REQUEST_CREDIT_VIEW_ID, setCredit);
    AppUtils.resolveServiceObject(
      creditUpdateMultipleServiceObject,
      REQUEST_CREDIT_UPDATE_MULTIPLE_PROPERTIES_ID,
      (data: any) => {
        const creditId = data?.id ?? credit?.id;
        if (creditId) {
          CreditService.creditsOne(creditId);
        }
      },
    );
    AppUtils.resolveServiceObject(
      creditOptionsServiceObject,
      REQUEST_CREDIT_OPTIONS_ID,
      setOptions,
    );
    AppUtils.resolveServiceObject(creditNewServiceObject, REQUEST_CREDIT_NEW_ID, setCredit);
    AppUtils.resolveServiceObject(
      postponeInstallmentServiceObject,
      REQUEST_CREDIT_POSTPONE_INSTALLMENT_ID,
      (data) => {
        setCredit((old) => ({ ...old!, postponedInstallments: data }));
      },
    );
    AppUtils.resolveServiceObject(
      bankCreditServiceObject,
      REQUEST_BANK_CREDIT_VIEW_ID,
      setBankCredit,
    );
    AppUtils.resolveServiceObject(
      bankCreditNewServiceObject,
      REQUEST_BANK_CREDIT_NEW_ID,
      (data) => {
        setBankCredit(data);
        setCredit((old) => ({ ...old!, bankCreditRequestId: data.id }));
        setIsCreditPreparationExpanded(false);
        setIsBankCreditExpanded(true);
      },
    );
    AppUtils.resolveServiceObject(
      bankCreditOptionsServiceObject,
      REQUEST_BANK_CREDIT_OPTIONS_ID,
      setBankCreditOptions,
    );
    AppUtils.resolveServiceObject(
      bankCreditUpdateMultipleServiceObject,
      REQUEST_BANK_CREDIT_UPDATE_MULTIPLE_PROPERTIES_ID,
      (data: any) => {
        const bankCreditId = data?.id ?? bankCredit?.id;
        if (bankCreditId) {
          BankCreditService.bankCreditOne(bankCreditId);
        }
      },
    );
  }, [
    clientServiceObject,
    creditStatusServiceObject,
    creditServiceObject,
    credit?.id,
    creditUpdateMultipleServiceObject,
    creditOptionsServiceObject,
    creditNewServiceObject,
    creditRequestServiceObject,
    postponeInstallmentServiceObject,
    bankCreditServiceObject,
    bankCreditNewServiceObject,
    bankCreditOptionsServiceObject,
    bankCreditUpdateMultipleServiceObject,
    bankCredit?.id,
    navigate,
    id,
  ]);

  useEffect(() => {
    if (credit?.bankCreditRequestId && !bankCredit) {
      BankCreditService.bankCreditOne(credit.bankCreditRequestId);
    }
  }, [bankCredit, credit?.bankCreditRequestId]);

  const tabTitle = useMemo(() => {
    if (!client) {
      return AppUtils.composeTabTitle(t("clients.view.title"));
    }
    return AppUtils.composeTabTitle(t("clients.view.title")) + " - " + client.fullName;
  }, [client, t]);

  const isLoading = useMemo(
    () =>
      (clientServiceObject && clientServiceObject.status === REQUEST_PENDING) ||
      (creditOptionsServiceObject && creditOptionsServiceObject.status === REQUEST_PENDING) ||
      (bankCreditOptionsServiceObject &&
        bankCreditOptionsServiceObject.status === REQUEST_PENDING) ||
      (bankCreditNewServiceObject && bankCreditNewServiceObject.status === REQUEST_PENDING),
    [
      clientServiceObject,
      creditOptionsServiceObject,
      bankCreditOptionsServiceObject,
      bankCreditNewServiceObject,
    ],
  );

  const isInitialLoading = useMemo(() => isLoading && !client, [isLoading, client]);

  const handleTabChange = (value: string | null) => {
    if (value === TABS.PREVIOUS_REQUESTS && previousRequestSelectedId !== null) {
      setPreviousRequestSelectedId(null);
    }
    setActiveTab(value);
  };

  const refreshClientCredit = useCallback(() => {
    if (credit?.id) {
      CreditService.creditsOne(credit.id);
    }
  }, [credit?.id]);

  const tabs = [
    {
      value: TABS.CURRENT_REQUEST,
      title: t("credit.current"),
      color: "#8AD572",
    },
    {
      value: TABS.PREVIOUS_REQUESTS,
      title: t("credit.previousRequests.title"),
      color: "#E0E2E6",
    },
  ];

  return (
    <LayoutStandard authority={AuthorityConstants.ROLE_CR_R} title={tabTitle}>
      <Box pb="lg">
        {isInitialLoading || !client ? (
          <Loader />
        ) : (
          <>
            <ClientDetails
              user={client!}
              credit={credit}
              agent={agent}
              unreadMessages={unreadMessages}
            />

            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant={"pills"}
              mt={"lg"}
              keepMounted={false}
            >
              <Tabs.List grow={true}>
                {tabs.map(({ value, title, color }) => (
                  <Tabs.Tab key={value} value={value} className={classes.tabView} color={color}>
                    {title}
                  </Tabs.Tab>
                ))}
              </Tabs.List>

              <Tabs.Panel value={TABS.CURRENT_REQUEST} className={classes.tabContent}>
                <CreditPreparationHeader
                  isExpanded={isCreditPreparationExpanded}
                  onToggle={() => setIsCreditPreparationExpanded((prevExpanded) => !prevExpanded)}
                />
                <Collapse in={isCreditPreparationExpanded} mb="md">
                  <ClientCredit user={client!} credit={credit} options={options} />
                </Collapse>
                {!!credit?.bankCreditRequestId && bankCredit && bankCreditOptions && (
                  <>
                    <BankCreditRequestHeader
                      creditAmount={bankCredit.creditAmount ?? credit.creditAmount}
                      paymentPeriod={bankCredit.paymentPeriod ?? credit.paymentPeriod}
                      monthlyInstallmentFrom={
                        bankCredit.monthlyInstallmentFrom ?? credit.monthlyInstallmentFrom
                      }
                      monthlyInstallmentTo={
                        bankCredit.monthlyInstallmentTo ?? credit.monthlyInstallmentTo
                      }
                      isExpanded={isBankCreditExpanded}
                      onToggle={() => setIsBankCreditExpanded((prevExpanded) => !prevExpanded)}
                    />
                    <Collapse in={isBankCreditExpanded} mb="md">
                      <BankCreditRequest
                        credit={bankCredit}
                        options={bankCreditOptions}
                        sourceCredit={credit}
                      />
                    </Collapse>

                    <BankSelectionHeader
                      isExpanded={isBankSelectionExpanded}
                      onToggle={() => setIsBankSelectionExpanded((prevExpanded) => !prevExpanded)}
                    />
                    <Collapse in={isBankSelectionExpanded}>
                      <BankSelection
                        key={credit.bankCreditRequestId}
                        clientId={client.id}
                        clientFullName={client.fullName}
                        creditId={credit.id}
                        bankCreditRequestId={credit.bankCreditRequestId}
                        bankCreditChildren={bankCredit.children}
                        availableBanks={Array.from(new Set(Object.values(options?.structureTypeBanks ?? {}).flat())) as Bank[]}
                        isExpanded={isBankSelectionExpanded}
                        clientAddress={{
                          street: client?.street,
                          houseNumber: client?.houseNumber,
                          postal: client?.postal,
                          city: client?.city,
                        }}
                        clientNationality={bankCredit?.nationality}
                        clientVisaType={bankCredit?.visaType}
                        onBankStatusUpdated={refreshClientCredit}
                      />
                    </Collapse>
                  </>
                )}
              </Tabs.Panel>
              <Tabs.Panel value={TABS.PREVIOUS_REQUESTS} className={classes.tabContent}>
                <PreviousCreditRequests
                  options={options}
                  navigateToCurrentRequest={() => setActiveTab(TABS.CURRENT_REQUEST)}
                  selectedCreditId={previousRequestSelectedId}
                  setSelectedCreditId={setPreviousRequestSelectedId}
                />
              </Tabs.Panel>
            </Tabs>
          </>
        )}
      </Box>
    </LayoutStandard>
  );
};

export default ClientViewPage;
